import React, { useEffect, useState, useRef } from 'react';
import styles from './PendingDataPill.module.scss';
import clsx from 'clsx';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import ArrowRight from '../../../../assets/arrow-right.svg';
import MuGuide from '../../../../assets/MuGuide.gif';
import CrossFilledIcon from '../../../../assets/Svg/crossFilledIcon';
import { magicUploadIconColor } from '../../../../assets/hostedassets';
import CrossIcon from '../../../../assets/Svg/CrossIcon';
import { useSearchParams } from 'react-router-dom';
import { updateQueryParams } from '../../../../utils/searchParamUtils';

interface PendingDataPillProps {
    mainText?: string;
    timeText?: string;
    showSpinner?: boolean;
    isGenerating?: boolean;
    ctaText?: string;
    isLoading?: boolean;
    showPendingData?: boolean;
    onClick?: () => void;
    expandedText?: string;
    expandedSubText?: string;
    gifSrc?: string;
    initialExpanded?: boolean;
    showLoadingAnimation?: boolean;
    hasPendingCount?: boolean;
    pendingCount?: number;
}

const PendingDataPill = ({
    mainText,
    timeText,
    ctaText,
    showSpinner = true,
    isGenerating = true,
    isLoading = false,
    showPendingData = false,
    onClick,
    expandedText = 'Magic Upload Data',
    expandedSubText = "Here's a helpful guide to get you started",
    gifSrc = MuGuide,
    initialExpanded = false,
    showLoadingAnimation = false,
    hasPendingCount,
    pendingCount,
}: PendingDataPillProps) => {
    // State for animations and transitions
    const [isExpanded, setIsExpanded] = useState(initialExpanded);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Refs for measuring content
    const expandedContentRef = useRef<HTMLDivElement>(null);
    const pillRef = useRef<HTMLDivElement>(null);

    // Handle loading animation
    useEffect(() => {
        if (isLoading && !isAnimating) {
            setIsAnimating(true);
            setIsComplete(false);

            const timer = setTimeout(() => {
                setIsComplete(true);
                setIsAnimating(false);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    useEffect(() => {
        const expandedPillFromStorage = localStorage.getItem('expandedPill');

        if (expandedPillFromStorage === 'false') {
            setIsExpanded(false);
        } else if (expandedPillFromStorage === null || expandedPillFromStorage === 'true') {
            setIsExpanded(true);
            updateQueryParams(searchParams, setSearchParams, { expandedPill: 'true' });
            localStorage.setItem('expandedPill', 'true');
        }
    }, []);

    // Close expanded view when clicking overlay
    const handleCloseExpanded = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded(false);
        searchParams.delete('expandedPill');
        updateQueryParams(searchParams, setSearchParams, {});
        localStorage.setItem('expandedPill', 'false');
    };

    // Handle click event
    const handleClick = (e: React.MouseEvent) => {
        handleCloseExpanded(e);
        setIsFullScreen(true);
        searchParams.set('settingFullScreen', 'true');
        updateQueryParams(searchParams, setSearchParams, { settingFullScreen: 'true' });

        onClick?.();
    };

    useEffect(() => {
        const showNeedInfo = searchParams.get('showNeedInfo');
        if (showNeedInfo === 'false' || showNeedInfo === null) {
            setIsFullScreen(false);
            searchParams.delete('settingFullScreen');
            setSearchParams(searchParams);
            updateQueryParams(searchParams, setSearchParams, {});
        }
    }, [searchParams.get('showNeedInfo')]);

    return (
        <>
            {(isExpanded || isFullScreen) && <div className={styles.overlay} onClick={handleCloseExpanded} />}

            <div
                ref={pillRef}
                className={clsx(styles.pill, {
                    [styles.expanded]: isExpanded,
                    [styles.fullScreen]: isFullScreen,
                    [styles.loading]: isAnimating,
                    [styles.noAnimation]: isAnimating && !showLoadingAnimation,
                    [styles.completed]: isComplete || (!isLoading && !isAnimating),
                })}
            >
                {/* Expanded content */}
                <div
                    ref={expandedContentRef}
                    className={clsx(styles.expandedContent, {
                        [styles.visible]: isExpanded,
                    })}
                    onClick={(e) => handleClick(e)}
                >
                    <div className={styles.closeButton} onClick={(e) => handleCloseExpanded(e)}>
                        <CrossIcon colour="#626262" />
                    </div>

                    <div className={styles.expandedHeader}>
                        <span className={styles.iconContainer}>
                            <img src={magicUploadIconColor} alt="Magic Upload Icon" width="24px" height="24px" />
                        </span>
                        <span className={styles.expandedTitle}>{expandedText}</span>
                    </div>

                    <div className={styles.expandedSubText}>{expandedSubText}</div>

                    <div className={styles.gifContainer}>
                        <img src={gifSrc} alt="Guide" className={styles.gif} />
                    </div>
                </div>

                {/* Pill content (always visible) */}
                <div className={styles.pillContent} onClick={(e) => handleClick(e)}>
                    <div className={styles.leftContent}>
                        {mainText && (
                            <div className={styles.mainText}>
                                {isGenerating ? (
                                    <>
                                        {mainText}
                                        <span className={styles.ellipsis} />
                                    </>
                                ) : hasPendingCount ? (
                                    <>Pending Data Requirements</>
                                ) : (
                                    mainText
                                )}
                            </div>
                        )}
                    </div>

                    <div
                        className={clsx(styles.rightContent, {
                            [styles.noHover]: showSpinner,
                        })}
                    >
                        {ctaText && <span className={styles.ctaText}>{ctaText}</span>}

                        {(showSpinner || showPendingData) && (
                            <div className={styles.iconWrapper}>
                                {showSpinner ? (
                                    <LoadingSpinner color="var(--primary-text-colour)" height="20px" />
                                ) : (
                                    <img src={ArrowRight} alt="Right Arrow" style={{ width: '20px', height: '20px' }} />
                                )}
                            </div>
                        )}
                    </div>

                    {timeText && <div className={styles.timeText}>{timeText}</div>}
                </div>
            </div>
        </>
    );
};

export default PendingDataPill;
